/*
 * cm-spin-360 — drag-to-rotate 360 viewer.
 *
 * Frames are discrete images rather than a scrubbed <video> on purpose: the
 * source render carries only 4 keyframes per 96, so seeking a video would
 * decode up to 24 frames per step and stutter, worst on iOS Safari.
 *
 * Frames are painted to a <canvas> rather than by swapping img.src. Setting
 * src clears the element until the replacement decodes, which reads as a black
 * flash on the first pass through the sequence; drawImage never blanks.
 *
 * Progressive enhancement: the markup ships a normal <img> of frame 1, so with
 * JS off or frames still loading the section is simply a product photo. The
 * canvas sits over that <img>, and the <img> is only hidden once a frame has
 * actually been painted.
 */
(() => {
  if (customElements.get('cm-spin-360')) return;

  const COARSE_STEP = 6; // load every 6th frame first, then backfill
  const NEAR_RADIUS = 3; // never snap further than this to find a loaded frame
  const MIN_TRAVEL = 560; // px of drag per full rotation; a floor for phones
  const MAX_DPR = 2;

  class CmSpin360 extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-cm-spin-frames]');
      if (!data) return;
      try {
        this.urls = JSON.parse(data.textContent);
      } catch (e) {
        return;
      }
      this.img = this.querySelector('[data-cm-spin-img]');
      this.stage = this.querySelector('[data-cm-spin-stage]');
      if (!this.img || !this.stage || !this.urls || this.urls.length < 2) return;

      this.count = this.urls.length;
      const startIndex = parseInt(this.dataset.startIndex, 10);
      this.index = Number.isFinite(startIndex)
        ? ((startIndex % this.count) + this.count) % this.count
        : 0;
      this.drawn = -1;
      this.cache = new Array(this.count);
      this.failed = new Array(this.count);
      this.loaded = 0;
      this.interactive = false;
      this.painted = false;
      this.pending = false;
      this.dragging = false;
      this.reverse = this.dataset.reverse === 'true';
      this.autoRotate = this.dataset.autoRotate === 'true';

      this.setAttribute('role', 'slider');
      this.setAttribute('tabindex', '0');
      this.setAttribute('aria-valuemin', '0');
      this.setAttribute('aria-valuemax', '359');
      this.updateAria();

      this.makeCanvas();
      this.observe();
      this.bind();
    }

    disconnectedCallback() {
      if (this.autoRotateFrame) cancelAnimationFrame(this.autoRotateFrame);
    }

    /* ---- auto-rotate ---------------------------------------------------
       An ambient, ownerless rotation for a viewer nobody is expected to
       drag (e.g. a paired "back" viewer next to the one the visitor
       actually controls). Pauses for as long as a drag is in progress —
       bind() sets/clears this.dragging — and resumes on release, rather
       than fighting the visitor's input. */
    startAutoRotate() {
      const STEP_MS = 55;
      let last = 0;
      const loop = (ts) => {
        if (!this.isConnected) return;
        if (!this.dragging && ts - last >= STEP_MS) {
          last = ts;
          this.show(this.index + (this.reverse ? -1 : 1));
        }
        this.autoRotateFrame = requestAnimationFrame(loop);
      };
      this.autoRotateFrame = requestAnimationFrame(loop);
    }

    /* ---- canvas ------------------------------------------------------- */

    makeCanvas() {
      const canvas = document.createElement('canvas');
      canvas.className = 'cm-spin__canvas';
      canvas.setAttribute('aria-hidden', 'true');
      this.canvas = canvas;
      this.ctx = canvas.getContext('2d');
      if (!this.ctx) return; // no 2d context: the plain <img> stays as-is
      this.stage.appendChild(canvas);
      this.resize();

      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => this.resize());
        this.ro.observe(this.stage);
      } else {
        window.addEventListener('resize', () => this.resize());
      }
    }

    // Backing store follows the rendered size, capped at the frame's own
    // resolution — upscaling past it just costs memory and fill rate.
    resize() {
      if (!this.ctx) return;
      const css = this.stage.clientWidth;
      if (!css) return;
      const nw = this.img.naturalWidth || parseInt(this.img.getAttribute('width'), 10) || css;
      const nh = this.img.naturalHeight || parseInt(this.img.getAttribute('height'), 10) || css;
      const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
      const w = Math.max(1, Math.min(Math.round(css * dpr), nw));
      const h = Math.max(1, Math.round(w * (nh / nw)));
      if (w === this.canvas.width && h === this.canvas.height) return;
      this.canvas.width = w;
      this.canvas.height = h;
      this.drawn = -1; // resizing clears the canvas
      this.paint();
    }

    /* ---- loading ------------------------------------------------------ */

    // Never start fetching the sequence before the page has finished loading.
    // As the product page's main image this element is in view immediately, so
    // without this the 16 coarse frames race the hero image for bandwidth and
    // push out LCP. Capped so a slow third-party script cannot leave the viewer
    // inert for long.
    afterLoad(fn) {
      if (document.readyState === 'complete') return fn();
      let done = false;
      const go = () => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        fn();
      };
      const timer = setTimeout(go, 3000);
      window.addEventListener('load', go, { once: true });
    }

    observe() {
      const start = () => this.afterLoad(() => this.preload());
      if (!('IntersectionObserver' in window)) return start();
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            io.disconnect();
            start();
          }
        },
        { rootMargin: '200px' }
      );
      io.observe(this);
    }

    // decode: wait for the frame to be decoded, not merely downloaded. Only
    // worth the memory for the coarse set — those are the frames we must be
    // able to paint the instant a drag starts.
    fetchFrame(i, decode) {
      if (this.cache[i] || this.failed[i]) return Promise.resolve();
      return new Promise((resolve) => {
        const im = new Image();
        im.decoding = 'async';
        im.onerror = () => {
          this.failed[i] = true;
          resolve();
        };
        im.onload = () => {
          this.cache[i] = im;
          this.loaded += 1;
          if (!decode || typeof im.decode !== 'function') return resolve();
          im.decode().then(resolve, resolve);
        };
        im.src = this.urls[i];
      });
    }

    async preload() {
      const coarse = [];
      for (let i = 0; i < this.count; i += COARSE_STEP) coarse.push(i);
      await Promise.all(coarse.map((i) => this.fetchFrame(i, true)));
      this.interactive = true;
      this.classList.add('cm-spin--ready');
      this.paint();
      if (this.autoRotate) this.startAutoRotate();

      // Backfill by offset rather than in index order, so the whole rotation
      // densifies at once. Filling 1,2,3,… instead leaves the far side of the
      // ring coarse for seconds, which is where a jumpy early drag comes from.
      const rest = [];
      for (const offset of [3, 1, 4, 2, 5]) {
        for (let i = offset; i < this.count; i += COARSE_STEP) {
          if (!this.cache[i] && !this.failed[i]) rest.push(i);
        }
      }
      for (let i = 0; i < this.count; i += 1) {
        if (!this.cache[i] && !this.failed[i] && rest.indexOf(i) < 0) rest.push(i);
      }
      for (let i = 0; i < rest.length; i += 8) {
        await Promise.all(rest.slice(i, i + 8).map((j) => this.fetchFrame(j, false)));
        // a frame we could only approximate may have just arrived
        if (this.drawn !== this.index) this.request();
      }
      this.classList.add('cm-spin--complete');
    }

    /* ---- painting ----------------------------------------------------- */

    // Bounded on purpose. An unbounded search snaps to whichever frame happens
    // to be loaded, which during backfill can be most of a rotation away — the
    // rotation appears to stick, then jump. Within the coarse step there is
    // always a hit; past that, holding the current frame is the lesser evil.
    nearestLoaded(i) {
      if (this.cache[i]) return i;
      for (let d = 1; d <= NEAR_RADIUS; d += 1) {
        const a = (i - d + this.count) % this.count;
        if (this.cache[a]) return a;
        const b = (i + d) % this.count;
        if (this.cache[b]) return b;
      }
      return -1;
    }

    show(i) {
      const next = ((i % this.count) + this.count) % this.count;
      this.index = next;
      this.request();
    }

    // Pointer events fire far faster than the display refreshes; coalescing to
    // one paint per frame is what keeps a fast drag smooth.
    request() {
      if (this.pending) return;
      this.pending = true;
      requestAnimationFrame(() => {
        this.pending = false;
        this.updateAria();
        this.paint();
      });
    }

    paint() {
      const draw = this.nearestLoaded(this.index);
      if (draw < 0 || draw === this.drawn) return;
      const im = this.cache[draw];
      if (!im || !im.naturalWidth) return;
      if (!this.ctx) {
        this.img.src = im.src; // no 2d context: swap the <img> and accept the flash
        this.drawn = draw;
        return;
      }
      if (!this.canvas.width) return; // stage not laid out yet; resize() repaints
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.ctx.drawImage(im, 0, 0, this.canvas.width, this.canvas.height);
      this.drawn = draw;
      if (!this.painted) {
        this.painted = true;
        this.classList.add('cm-spin--canvas');
      }
    }

    updateAria() {
      const deg = Math.round((this.index / this.count) * 360);
      if (this.getAttribute('aria-valuenow') === String(deg)) return;
      this.setAttribute('aria-valuenow', String(deg));
      this.setAttribute('aria-valuetext', `Rotated ${deg} degrees`);
    }

    /* ---- interaction -------------------------------------------------- */

    bind() {
      let startX = 0;
      let startIndex = 0;

      // A full rotation spans 1.2x the viewer's width, floored at MIN_TRAVEL so
      // a narrow phone does not turn a flick into several rotations.
      const perPixel = () => {
        const w = this.stage ? this.stage.clientWidth : this.clientWidth;
        return this.count / Math.max(w * 1.2, MIN_TRAVEL);
      };

      this.addEventListener('pointerdown', (e) => {
        if (!this.interactive || !e.isPrimary) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        this.dragging = true;
        startX = e.clientX;
        startIndex = this.index;
        try { this.setPointerCapture(e.pointerId); } catch (err) { /* stale pointer */ }
        this.classList.add('cm-spin--dragging');
      });

      this.addEventListener('pointermove', (e) => {
        if (!this.dragging) return;
        const dx = e.clientX - startX;
        const delta = dx * perPixel() * (this.reverse ? -1 : 1);
        this.show(Math.round(startIndex + delta));
      });

      const end = (e) => {
        if (!this.dragging) return;
        this.dragging = false;
        try {
          if (e.pointerId != null && this.hasPointerCapture(e.pointerId)) {
            this.releasePointerCapture(e.pointerId);
          }
        } catch (err) { /* already released */ }
        this.classList.remove('cm-spin--dragging');
      };
      this.addEventListener('pointerup', end);
      this.addEventListener('pointercancel', end);

      // no wheel handler on purpose: the page must scroll normally over this
      this.addEventListener('keydown', (e) => {
        if (!this.interactive) return;
        const step = e.shiftKey ? 5 : 1;
        if (e.key === 'ArrowLeft') { this.show(this.index - step); e.preventDefault(); }
        else if (e.key === 'ArrowRight') { this.show(this.index + step); e.preventDefault(); }
        else if (e.key === 'Home') { this.show(0); e.preventDefault(); }
      });

      this.img.addEventListener('dragstart', (e) => e.preventDefault());
    }
  }

  customElements.define('cm-spin-360', CmSpin360);
})();
